import request from "../helpers/request";
import config from "../settings";

const defaultOptions = { credentials: "same-origin", headers: {} };
const methodsWithPayload = ["POST", "PUT", "PATCH", "DELETE"];
const API_URL = `${config.baseUrl}/api`;

/* eslint-disable no-underscore-dangle */
function _apiCall(path, options = {}) {
  const defaultedOptions = Object.assign({}, defaultOptions, options);

  if (
    methodsWithPayload.indexOf(defaultedOptions.method) > -1 &&
    defaultedOptions.body
  ) {
    defaultedOptions.headers["Content-Type"] = "application/json";
    defaultedOptions.body =
      typeof defaultedOptions.body === "string"
        ? defaultedOptions.body
        : JSON.stringify(defaultedOptions.body);
  }
  if (defaultedOptions.auth) {
    defaultedOptions.headers.Authorization = `Bearer ${getUserAuthToken()}`;
  }

  let defaultedPath = typeof path === "string" ? path : path.join("/");

  if (defaultedOptions.params) {
    const esc = encodeURIComponent;
    const query = Object.keys(defaultedOptions.params)
      .map((k) => `${esc(k)}=${esc(defaultedOptions.params[k])}`)
      .join("&");
    defaultedPath = `${defaultedPath}?${query}`;
  }

  return request(`${API_URL}/${defaultedPath}`, defaultedOptions).then(
    (result) => result
  );
}

export function getUserAuthToken() {
  return localStorage.getItem("id_token");
}

function makeRequest(method, url, file) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(method, url);
    // xhr.setRequestHeader('Cache-Control', 'public,max-age=3600');
    xhr.setRequestHeader("Authorization", `Bearer ${getUserAuthToken()}`);
    xhr.onload = function() {
      if (this.status >= 200 && this.status < 300) {
        // let data = url;
        resolve(JSON.parse(xhr.response));
      } else {
        reject({
          status: this.status,
          error: { message: JSON.parse(xhr.response).error.message },
        });
      }
    };

    xhr.onerror = function() {
      reject({
        status: this.status,
        statusText: xhr.statusText,
      });
    };
    xhr.send(file);
  });
}

let callId = 0;
export const call =
  process.env.NODE_ENV === "production"
    ? (path, options = {}, json = true, ...rest) => {
        callId += 1;
        const thisCallId = callId;
        return _apiCall(path, options, json, ...rest).then(
          (result) => {
            return result;
          },
          (error) => {
            if (error && error.message === "Invalid token") {
              window.location.replace("/signin");
            }
            throw error;
          }
        );
      }
    : (path, options = {}, json = true, ...rest) => {
        callId += 1;
        const thisCallId = callId;
        /* eslint-disable no-console */
        return _apiCall(path, options, json, ...rest).then(
          (result) => {
            return result;
          },
          (error) => {
            if (error && error.message === "Invalid token") {
              window.location.replace("/signin");
            }
            console.error(
              "API Error",
              thisCallId,
              error.stack || error.message || error
            );
            throw error;
          }
        );
        /* eslint-enable no-console */
      };

export default {
  auth: {
    login(payload) {
      return call(["auth", "login"], {
        method: "POST",
        body: payload,
      });
    },
    logout() {
      return call(["auth", "signout"], {
        method: "GET",
        auth: true,
      });
    },
  },
  common: {
    fetchHashTags(qs) {
      return call(["admin", "hashtag"], {
        method: "GET",
        auth: true,
        params: {
          q: qs,
        },
      });
    },
    fetchFields(payload) {
      return call(["admin", "settings"], {
        method: "GET",
        auth: true,
        ...(payload ? { params: payload } : null),
      });
    },
    updateSettings(payload) {
      return call(["admin", "settings"], {
        method: "PATCH",
        auth: true,
        body: payload,
      });
    },
    updateAdminSettings(payload) {
      return call(["admin", "settings", "superAdmin"], {
        method: "PATCH",
        auth: true,
        body: payload,
      });
    },
    fetchAdminSettings() {
      return call(["admin", "settings", "superAdmin"], {
        method: "GET",
        auth: true,
      });
    },
    version() {
      return call(["admin", "device", "app-versions"], {
        method: "GET",
        auth: true,
      });
    },
    schools() {
      return call(["admin", "school"], {
        method: "GET",
        auth: true,
      });
    },
    teachers(id) {
      return call(["user", "teachers"], {
        method: "GET",
        auth: true,
        params: id
          ? {
              school: id,
            }
          : {},
      });
    },

    admin(body) {
      return call(["admin", "user", "autoComplete", "v2"], {
        method: "POST",
        body,
        auth: true,
      });
    },
    students(id) {
      return call(["user", "students"], {
        method: "GET",
        auth: true,
        params: {
          school: id,
        },
      });
    },
    users(body) {
      return call(["user", "autoComplete", "v2"], {
        method: "POST",
        body,
        auth: true,
      });
    },
    subjects() {
      return call(["admin", "subject"], {
        method: "GET",
        auth: true,
      });
    },
    subjectsV2() {
      return call(["admin", "subject", "v2"], {
        method: "GET",
        auth: true,
        params: {
          export: true,
        },
      });
    },
    grades() {
      return call(["admin", "grade"], {
        method: "GET",
        auth: true,
      });
    },
    gradesV2() {
      return call(["admin", "grade", "v2"], {
        method: "GET",
        auth: true,
        params: {
          exports: true,
        },
      });
    },
    roles() {
      return call(["admin", "role"], {
        method: "GET",
        auth: true,
      });
    },
    import(payload) {
      return makeRequest("POST", `${API_URL}/admin/custom/import/`, payload);
    },
    encrypt(payload) {
      return makeRequest("POST", `${API_URL}/admin/custom/encrypt/`, payload);
    },
    saveDoe(payload) {
      return makeRequest("POST", `${API_URL}/admin/custom/doe/`, payload);
    },
    getDoe() {
      return call(["admin", "custom", "doe"], {
        method: "GET",
        auth: true,
      });
    },
  },
  dashboard: {
    list(body) {
      return call(["admin", "dashboard"], {
        method: "POST",
        body,
        auth: true,
      });
    },
    list1(params) {
      return call(["admin", "dashboard"], {
        method: "GET",
        params,
        auth: true,
      });
    },
    getAbsconding(params) {
      return call(["admin", "dashboard", "maintenance", "absconding"], {
        method: "GET",
        params,
        auth: true,
      });
    },
    getSuspiciousApps(params) {
      return call(["admin", "dashboard", "maintenance", "suspecious-apps"], {
        method: "GET",
        params,
        auth: true,
      });
    },
  },
  admin: {
    getList(payload = {}) {
      return call(["admin", "user", "autoComplete", "v2"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    tableList(payload = {}) {
      return call(["admin", "user", "v2"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },

    list(params) {
      return call(["admin", "user"], {
        method: "GET",
        auth: true,
        params,
      });
    },
    update(payload, userId) {
      return call(["admin", "user", userId], {
        method: "PATCH",
        body: payload,
        auth: true,
      });
    },
    details(id) {
      return call(["admin", "user", id], {
        method: "GET",
        auth: true,
      });
    },
    add(payload) {
      return call(["admin", "user"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    delete(userId) {
      return call(["admin", "user", userId], {
        method: "DELETE",
        auth: true,
      });
    },
  },
  users: {
    list(payload) {
      return call(["user"], {
        method: "GET",
        auth: true,
        ...(payload
          ? {
              params: payload,
            }
          : {}),
      });
    },
    add(payload) {
      return call(["user"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    bulk_update(payload) {
      return call(["user", "bulk-edit"], {
        method: "PUT",
        body: payload,
        auth: true,
      });
    },
    update(payload, userId) {
      return call(["user", userId], {
        method: "PATCH",
        body: payload,
        auth: true,
      });
    },
    delete(userId) {
      return call(["user", userId], {
        method: "DELETE",
        auth: true,
      });
    },
    details(id) {
      return call(["user", id], {
        method: "GET",
        auth: true,
      });
    },
    uploadAvatar(payload) {
      return makeRequest("POST", `${API_URL}/user/set/avatar`, payload);
    },
    search(payload) {
      return call(["user", "search"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    searchV2(payload) {
      return call(["user", "v2", "search"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    autocompleteUserV2(payload) {
      return call(["user", "autoComplete", "v2"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    searchPagination(payload) {
      return call(["user", "v2", "search"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    notification(payload) {
      return call(["user", "notification", "v2"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    notificationDetails(id) {
      return call(["user", "notification", id], {
        method: "GET",
        auth: true,
        params: {
          notificationId: id,
        },
      });
    },
    tableList(payload = {}) {
      return call(["user", "v2", "notification"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    notificationDownload(payload = {}) {
      return call(["user", "notification", "export", "summary"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    notificationItemDownload(id) {
      return call(["user", "notification", "export", id], {
        method: "GET",
        auth: true,
        // params: payload,
      });
    },
    listNotification() {
      return call(["user", "notification"], {
        method: "GET",
        auth: true,
      });
    },
    forgotPassword(payload) {
      return call(["auth", "forgotPassword"], {
        method: "POST",
        body: payload,
      });
    },
    resetPassword(payload) {
      return call(["auth", "resetPassword"], {
        method: "POST",
        body: payload,
      });
    },
    multiDelete(userId) {
      return call(["user", "bulk-delete"], {
        method: "DELETE",
        body: { userId },
        auth: true,
      });
    },
  },
  schools: {
    tableList(payload = {}) {
      return call(["admin", "school", "v2"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    list() {
      return call(["admin", "school"], {
        method: "GET",
        auth: true,
      });
    },
    add(payload) {
      return call(["admin", "school"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    update(payload, schoolId) {
      return call(["admin", "school", schoolId], {
        method: "PATCH",
        body: payload,
        auth: true,
      });
    },
    details(schoolId) {
      return call(["admin", "school", schoolId], {
        method: "GET",
        auth: true,
      });
    },
  },
  test: {
    tableList(payload = {}) {
      return call(["admin", "mcq", "list-tests", "v2"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },

    list() {
      return call(["admin", "mcq", "list-tests"], {
        method: "GET",
        auth: true,
      });
    },
    report(params) {
      return call(["admin", "mcq", "export-test"], {
        method: "GET",
        auth: true,
        params,
      });
    },
    add(payload) {
      return makeRequest(
        "POST",
        `${API_URL}/admin/mcq/insert-tests/v2/`,
        payload
      );
    },
    uploadAssertion(payload) {
      return makeRequest(
        "POST",
        `${API_URL}/admin/mcq/users-test-assign/`,
        payload
      );
    },
    TestAssignUser(payload) {
      return call(["admin", "mcq", "users-test-assign-user-id-wise"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    update(payload) {
      return call(["admin", "mcq", "update-test"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    delete(testid) {
      return call(["admin", "mcq", "insert-tests"], {
        method: "DELETE",
        body: testid,
        auth: true,
      });
    },
    getReport(testid) {
      return call(["admin", "mcq", "usersBytest"], {
        method: "GET",
        params: { testid },
        auth: true,
      });
    },
    byFilter() {
      return call(["admin", "mcq", "getTestsForLesson", "v2"], {
        method: "GET",
        auth: true,
      });
    },
    publish(body) {
      return call(["admin", "mcq", "publish"], {
        method: "PATCH",
        body,
        auth: true,
      });
    },
  },
  role: {
    tableList(payload = {}) {
      return call(["admin", "role", "v2", "admins"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },

    list() {
      return call(["admin", "role", "admins"], {
        method: "GET",
        auth: true,
      });
    },
    listMenu() {
      return call(["admin", "menu"], {
        method: "GET",
        auth: true,
      });
    },
    addRole(payload) {
      return call(["admin", "role"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    delete(id) {
      return call(["admin", "role", id], {
        method: "DELETE",
        auth: true,
      });
    },
    update(payload, id) {
      return call(["admin", "role", id], {
        method: "PATCH",
        body: payload,
        auth: true,
      });
    },
  },
  survey: {
    tableList(payload = {}) {
      return call(["admin", "mcq/list-survay", "v2"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },

    list() {
      return call(["admin", "mcq", "list-survay"], {
        method: "GET",
        auth: true,
      });
    },
    add(payload) {
      return makeRequest("POST", `${API_URL}/admin/mcq/survay/`, payload);
    },
    uploadAssertion(payload) {
      return makeRequest(
        "POST",
        `${API_URL}/admin/mcq/users-survey-assign/`,
        payload
      );
    },
    update(payload) {
      return call(["admin", "mcq", "update-survay"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    delete(surveyid) {
      return call(["admin", "mcq", "survay"], {
        method: "DELETE",
        body: surveyid,
        auth: true,
      });
    },
    getReport(surveyid) {
      return call(["admin", "mcq", "usersBysurvey"], {
        method: "GET",
        params: { surveyid },
        auth: true,
      });
    },
    surveyAccessionUser(payload) {
      return call(["admin", "mcq", "users-survey-assign-user-id-wise"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
  },
  rewards: {
    tableList(payload = {}) {
      return call(["admin", "reward", "v2"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    update(payload, id) {
      return call(["admin", "reward", id], {
        method: "PATCH",
        body: payload,
        auth: true,
      });
    },
    list(params) {
      return call(["admin", "reward"], {
        method: "GET",
        auth: true,
        params,
      });
    },
    create(payload) {
      return makeRequest("POST", `${API_URL}/admin/reward`, payload);
    },
  },

  groups: {
    list(payload) {
      return call(["admin", "chat", "group"], {
        method: "GET",
        auth: true,
        ...(payload
          ? {
              params: payload,
            }
          : {}),
      });
    },
    history(payload) {
      return call(["admin", "chat", "history"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    add(payload) {
      return call(["admin", "chat", "group"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    update(payload) {
      return call(["admin", "chat", "group"], {
        method: "PUT",
        body: payload,
        auth: true,
      });
    },
    delete(groupid) {
      return call(["admin", "chat", "group"], {
        method: "DELETE",
        body: groupid,
        auth: true,
      });
    },
    sendChatReply(payload) {
      return call(["admin", "chat", "sendMessage"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    chatExport() {
      return call(["admin", "chat", "exportChat"], {
        method: "GET",
        auth: true,
      });
    },
  },

  subjects: {
    tableList(payload = {}) {
      return call(["admin", "subject", "v2"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    listV2(payload) {
      return call(["admin", "subject", "autoComplete"], {
        method: "POST",
        auth: true,
        body: payload,
      });
    },
    list() {
      return call(["admin", "subject"], {
        method: "GET",
        auth: true,
      });
    },
    add(payload) {
      return makeRequest("POST", `${API_URL}/admin/subject/`, payload);
    },
    update(payload, subjectId) {
      return makeRequest(
        "PATCH",
        `${API_URL}/admin/subject/${subjectId}`,
        payload
      );
    },
    delete(subjectId) {
      return call(["admin", "subject", subjectId], {
        method: "DELETE",
        auth: true,
      });
    },
  },

  type: {
    tableList(payload = {}) {
      return call(["admin", "ticketType", "v2"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    add(payload) {
      return call(["admin", "ticketType", "v2"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    delete(ticketAreaId) {
      return call(["admin", "ticketType", "v2", ticketAreaId], {
        method: "DELETE",
        auth: true,
      });
    },
    update(payload) {
      return call(["admin", "ticketType", "v2"], {
        method: "PATCH",
        body: payload,
        auth: true,
      });
    },
  },
  area: {
    tableList(payload = {}) {
      return call(["admin", "ticketArea", "v2"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    add(payload) {
      return call(["admin", "ticketArea", "v2"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    delete(ticketAreaId) {
      return call(["admin", "ticketArea", "v2", ticketAreaId], {
        method: "DELETE",
        auth: true,
      });
    },
    update(payload) {
      return call(["admin", "ticketArea", "v2"], {
        method: "PATCH",
        body: payload,
        auth: true,
      });
    },
  },
  grades: {
    tableList(payload = {}) {
      return call(["admin", "grade", "v2"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    listV2(payload) {
      return call(["admin", "grade", "autoComplete"], {
        method: "POST",
        auth: true,
        body: payload,
      });
    },
    list() {
      return call(["admin", "grade"], {
        method: "GET",
        auth: true,
      });
    },
    add(payload) {
      return call(["admin", "grade"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    update(payload, gradeId) {
      return call(["admin", "grade", gradeId], {
        method: "PATCH",
        body: payload,
        auth: true,
      });
    },
  },
  books: {
    exportBonus() {
      return call(["admin", "website", "bonus", "export"], {
        method: "GET",
        auth: true,
      });
    },
    tableList(payload = {}) {
      return call(["admin", "resource", "v2"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    list() {
      return call(["admin", "resource"], {
        method: "GET",
        auth: true,
      });
    },
    search(payload) {
      return call(["admin", "resource", "search"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    add(payload) {
      return makeRequest("POST", `${API_URL}/admin/resource/v2`, payload);
    },
    updateV2(payload, resourceId) {
      return makeRequest(
        "PATCH",
        `${API_URL}/admin/resource/v2/${resourceId}`,
        payload
      );
    },
    update(payload, resourceId) {
      return makeRequest(
        "PATCH",
        `${API_URL}/admin/resource/${resourceId}`,
        payload
      );
    },
    delete(resourceId) {
      return call(["admin", "resource", resourceId], {
        method: "DELETE",
        auth: true,
      });
    },
    details(resourceId) {
      return call(["admin", "resource", resourceId], {
        method: "GET",
        auth: true,
      });
    },
    version: {
      add(payload, bookId) {
        return makeRequest(
          "POST",
          `${API_URL}/admin/resource/version/${bookId}`,
          payload
        );
      },
      delete(versionId) {
        return call(["admin", "resource", "version", versionId], {
          method: "DELETE",
          auth: true,
        });
      },
    },
  },
  bonusContent: {
    list() {
      return call(["admin", "resource", "bonus"], {
        method: "GET",
        auth: true,
      });
    },
    tableList(payload = {}) {
      return call(["admin", "resource", "bonus", "v2"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    search(payload) {
      return call(["admin", "resource", "bonus", "search"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    add(payload) {
      return makeRequest("POST", `${API_URL}/admin/resource/bonus/v2`, payload);
    },
    update(payload, resourceId) {
      return makeRequest(
        "PATCH",
        `${API_URL}/admin/resource/bonus/v2/${resourceId}`,
        payload
      );
    },
    delete(resourceId) {
      return call(["admin", "resource", "bonus", resourceId], {
        method: "DELETE",
        auth: true,
      });
    },
    details(resourceId) {
      return call(["admin", "resource", "bonus", resourceId], {
        method: "GET",
        auth: true,
      });
    },
    version: {
      add(payload, bookId) {
        return makeRequest(
          "POST",
          `${API_URL}/admin/resource/bonus/version/${bookId}`,
          payload
        );
      },
      delete(versionId) {
        return call(["admin", "resource", "bonus", "version", versionId], {
          method: "DELETE",
          auth: true,
        });
      },
    },
  },
  apps: {
    tableList(payload = {}) {
      return call(["admin", "custom", "v2", "apps"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    list() {
      return call(["admin", "custom", "apps"], {
        method: "GET",
        auth: true,
      });
    },
    save(payload) {
      return makeRequest("POST", `${API_URL}/admin/custom/apps`, payload);
    },
    update(payload, appId) {
      return makeRequest(
        "PATCH",
        `${API_URL}/admin/custom/apps/${appId}`,
        payload
      );
    },
    delete(appId) {
      return call(["admin", "custom", "apps", appId], {
        method: "DELETE",
        auth: true,
      });
    },
  },
  otherApps: {
    list() {
      return call(["admin", "custom", "other-apps"], {
        method: "GET",
        auth: true,
      });
    },
    save(payload) {
      return makeRequest("POST", `${API_URL}/admin/custom/other-apps`, payload);
    },
    update(payload, appId) {
      return makeRequest(
        "PATCH",
        `${API_URL}/admin/custom/other-apps/${appId}`,
        payload
      );
    },
    delete(appId) {
      return call(["admin", "custom", "other-apps", appId], {
        method: "DELETE",
        auth: true,
      });
    },
  },

  websites: {
    tableList(payload = {}) {
      return call(["admin", "website", "v2"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },

    list() {
      return call(["admin", "website"], {
        method: "GET",
        auth: true,
      });
    },
    add(payload) {
      return makeRequest("POST", `${API_URL}/admin/website/`, payload);
    },
    update(payload, websiteId) {
      return makeRequest(
        "PATCH",
        `${API_URL}/admin/website/${websiteId}`,
        payload
      );
    },
    delete(websiteId) {
      return call(["admin", "website", websiteId], {
        method: "DELETE",
        auth: true,
      });
    },
  },
  bonusWebsite: {
    tableList(payload = {}) {
      return call(["admin", "website", "v2", "bonus"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },

    list() {
      return call(["admin", "website", "bonus"], {
        method: "GET",
        auth: true,
      });
    },
    listSingle(id) {
      return call(["admin", "website", "bonus", id], {
        method: "GET",
        auth: true,
      });
    },
    add(payload) {
      return makeRequest("POST", `${API_URL}/admin/website/bonus`, payload);
    },
    update(payload, websiteId) {
      return makeRequest(
        "PATCH",
        `${API_URL}/admin/website/bonus/${websiteId}`,
        payload
      );
    },
    delete(websiteId) {
      return call(["admin", "website", "bonus", websiteId], {
        method: "DELETE",
        auth: true,
      });
    },
  },
  device: {
    tableList(payload = {}) {
      return call(["admin", "device", "v2"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    space(payload) {
      return call(["admin", "device", "storage-cleaning"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    downloadSheet(params) {
      return call(["admin", "summery"], {
        method: "GET",
        auth: true,
        params,
      });
    },
    list() {
      return call(["admin", "device"], {
        method: "GET",
        auth: true,
      });
    },
    summary(payload) {
      return call(["admin", "summery", "get"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    add(payload) {
      return call(["admin", "device"], {
        method: "POST",
        body: payload,
        auth: true,
      });
    },
    update(payload, deviceId) {
      return call(["admin", "device", deviceId], {
        method: "PATCH",
        body: payload,
        auth: true,
      });
    },
    delete(deviceId) {
      return call(["admin", "device", deviceId], {
        method: "DELETE",
        auth: true,
      });
    },
    assignUser(payload, deviceId) {
      return call(["admin", "device", "assign", deviceId], {
        method: "PATCH",
        body: payload,
        auth: true,
      });
    },
    multiUnpin(deviceid) {
      return call(["admin", "device", "bulk-unpin"], {
        method: "POST",
        body: { deviceid },
        auth: true,
      });
    },
    summaryButton() {
      return call(["admin", "summery"], {
        method: "GET",
        auth: true,
      });
    },
    location(body) {
      return call(["admin", "summery", "location"], {
        method: "POST",
        body,
        auth: true,
      });
    },
    sleepAwake(body) {
      return call(["admin", "summery", "sleepAwake"], {
        method: "POST",
        body,
        auth: true,
      });
    },
    multiUnpin(deviceid) {
      return call(["admin", "device", "bulk-unpin"], {
        method: "POST",
        body: { deviceid },
        auth: true,
      });
    },
    summaryButton() {
      return call(["admin", "summery"], {
        method: "GET",
        auth: true,
      });
    },
    locationUpdate(body) {
      return call(["admin", "device", "bulk-location-update"], {
        method: "PATCH",
        body,
        auth: true,
      });
    },
    getLocation(id) {
      return call(["admin", "device", "bulk-location-update", id], {
        method: "GET",
        auth: true,
      });
    },
    export(id) {
      return call(["admin", "device", "print-bulk-location-update", id], {
        method: "GET",
        auth: true,
      });
    },
    deviceDetails(id) {
      return call(["admin", "device", "bulk-location-update", id], {
        method: "GET",
        auth: true,
      });
    },
    download(payload) {
      return call(["admin", "device", "bulk-location-update"], {
        method: "GET",
        params: payload,
      });
    },
    exportTransaction(body) {
      return call(["admin", "device", "bulk-location-update", "export"], {
        method: "POST",
        body,
        auth: true,
      });
    },
  },
  health: {
    delete(payload) {
      return call(["admin", "device", "gallary-cleaning"], {
        method: "POST",
        auth: true,
        body: payload,
      });
    },
    tableList(payload = {}) {
      return call(["admin", "health", "v3"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    create(payload) {
      return call(["admin", "health"], {
        method: "POST",
        auth: true,
        body: payload,
      });
    },
    list() {
      return call(["admin", "health"], {
        method: "GET",
        auth: true,
      });
    },
    listSearch(payload) {
      return call(["admin", "health"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },

    Areas() {
      return call(["admin", "ticketArea", "v2", "technicalSupport"], {
        method: "GET",
        auth: true,
        params: { export: true },
      });
    },
    Types() {
      return call(["admin", "ticketType", "v2", "technicalSupport"], {
        method: "GET",
        auth: true,
        params: { export: true },
      });
    },
    getTypes() {
      return call(["admin", "ticketType", "v2"], {
        method: "GET",
        auth: true,
        params: { export: true },
      });
    },
    getAreas() {
      return call(["admin", "ticketArea", "v2"], {
        method: "GET",
        auth: true,
        params: { export: true },
      });
    },
    notification(payload) {
      return call(["admin", "health", "notification"], {
        method: "POST",
        auth: true,
        body: payload,
      });
    },
    updateCategory(payload) {
      return call(["admin", "health", payload.ticketId], {
        method: "PATCH",
        auth: true,
        body: {
          type: payload.type,
          area: payload.area,
        },
      });
    },
    toggle(payload) {
      return call(["admin", "health", payload.id], {
        method: "PATCH",
        auth: true,
        body: {
          isClosed: payload.isClosed,
          closedOrReopenedMsg: payload.closedOrReopenedMsg,
        },
      });
    },
    updateEscalation(id, payload) {
      return call(["admin", "health", "update-escalation", id], {
        method: "PATCH",
        body: payload,
        auth: true,
      });
    },
    getHealthDetails(id) {
      return call(["admin", "health", id], {
        method: "GET",
        auth: true,
      });
    },
  },
  accession: {
    list(payload) {
      return call(["user", "autoComplete", "v2"], {
        method: "POST",
        auth: true,
        body: payload,
      });
    },
  },
  psm: {
    tableList(payload = {}) {
      return call(["admin", "psm", "list-psm", "v2"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },

    list(params) {
      return call(["admin", "psm", "filter-pms"], {
        method: "GET",
        params,
        auth: true,
      });
    },
    export(body) {
      return call(["admin", "psm", "export-history"], {
        method: "POST",
        body,
        auth: true,
      });
    },
    listPsm(payload) {
      return call(["admin", "psm", "list-psm"], {
        method: "GET",
        auth: true,
        ...(payload
          ? {
              params: payload,
            }
          : {}),
      });
    },
    save(payload) {
      return makeRequest("POST", `${API_URL}/admin/psm`, payload);
    },
    savev2(payload) {
      return makeRequest("POST", `${API_URL}/admin/psm/v2`, payload);
    },
  },
  zoom: {
    save(body) {
      return call(["admin", "zoom-meeting"], {
        method: "POST",
        body,
        auth: true,
      });
    },
  },
  bbm: {
    tableList(payload = {}) {
      return call(["admin", "bbb", "v2"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    get(params) {
      return call(["admin", "bbb"], {
        method: "GET",
        auth: true,
        params,
      });
    },
    getDetails(id) {
      return call(["admin", "bbb", "v2", id], {
        method: "GET",
        auth: true,
      });
    },
    save(body) {
      return call(["admin", "bbb", "v2"], {
        method: "POST",
        body,
        auth: true,
      });
    },
    delete(id) {
      return call(["admin", "bbb", "v2", id], {
        method: "DELETE",
        auth: true,
      });
    },
    edit(payload, id) {
      return call(["admin", "bbb", "v2", id], {
        method: "PATCH",
        auth: true,
        body: payload,
      });
    },
    endCall(id) {
      return call(["admin", "bbb", "end", id], {
        method: "PATCH",
        auth: true,
      });
    },
    publish(id, body) {
      return call(["admin", "bbb", "record", "publish", id], {
        method: "PATCH",
        auth: true,
        body,
      });
    },
    deleteRecord(id) {
      return call(["admin", "bbb", "record", "delete", id], {
        method: "DELETE",
        auth: true,
      });
    },
    start(id) {
      return call(["admin", "bbb", "start", id], {
        method: "PATCH",
        auth: true,
      });
    },
    joinMeeting(id) {
      return call(["admin", "bbb", "join", id], {
        method: "GET",
        auth: true,
      });
    },
  },
  profile: {
    changePassword(body, userId) {
      return call(["admin", "user", "change-password"], {
        method: "POST",
        body,
        auth: true,
      });
    },
  },
  investors: {
    tableList(payload = {}) {
      return call(["admin", "investor"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    getInvestor(id) {
      return call(["admin", "investor", id], {
        method: "GET",
        auth: true,
      });
    },
    add(payload) {
      return call(["admin", "investor"], {
        method: "POST",
        body: payload,
      });
    },
    update(payload, investorId) {
      return call(["admin", "investor", investorId], {
        method: "PATCH",
        body: payload,
      });
    },
    allInvestors() {
      return call(["admin", "investor"], {
        method: "GET",
        auth: true,
      });
    },
    allProposals(params) {
      return call(["admin","investor","proposal","all"],{
        method: "GET",
        auth: true,
        params
      });
    },
    delete(investorId) {
      return call(["admin", "investor", investorId], {
        method: "DELETE",
        auth: true,
      });
    },
    lookups(props) {
      let params = { docNumber: props };
      return call(["admin", "investor", "lookup"], {
        method: "GET",
        auth: true,
        params,
      });
    },
    
    allDeviceTransactions(payload = {}) {
      return call(["admin", "device", "bulk-location-update"], {
        method: "POST",
        auth: true,
        body: payload,
      });
    },
  },
  investorProposal: {
    tableList(payload = {}) {
      return call(["admin", "investor", "proposal"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    add(payload) {
      return call(["admin", "investor", "proposal"], {
        method: "POST",
        body: payload,
      });
    },
    update(payload, investorId) {
      console.log(investorId);
      return call(["admin", "investor", "proposal", investorId], {
        method: "PATCH",
        body: payload,
      });
    },
    allInvestors() {
      return call(["admin", "investor"], {
        method: "GET",
        auth: true,
        // params: { export: false },
      });
    },
    delete(investorId) {
      return call(["admin", "investor", "proposal", investorId], {
        method: "DELETE",
        auth: true,
      });
    },
    lookups(props) {
      let params = { docNumber: props };
      return call(["admin", "investor", "proposal", "lookup"], {
        method: "GET",
        auth: true,
        params,
      });
    },
    fileUpload(payload) {
      console.log(payload);
      return call(["admin", "file-upload"], {
        method: "POST",
        auth: true,
        body: { file: payload },
      });
    },
    proposals(params) {
      return call(["admin","investor","proposal","all"],{
        method: "GET",
        auth: true,
        params
      });
    },
  },
  impactProposal: {
    // tableList(payload = {}) {
    //   return call(["admin", "investor", "proposal"], {
    //     method: "GET",
    //     auth: true,
    //     params: payload,
    //   });
    // },
    add(payload) {
      return call(["admin", "investor", "impact-proposal"], {
        method: "POST",
        body: payload,
      });
    },
    update(payload, investorId) {
      return call(["admin", "investor", "impact-proposal", investorId], {
        method: "PATCH",
        body: payload,
      });
    },
    allInvestors() {
      return call(["admin", "investor"], {
        method: "GET",
        auth: true,
        // params: { export: false },
      });
    },
    delete(investorId) {
      return call(["admin", "investor", investorId], {
        method: "DELETE",
        auth: true,
      });
    },
    lookups(props) {
      let params = { docNumber: props };
      return call(["admin", "investor", "lookup"], {
        method: "GET",
        auth: true,
        params,
      });
    },
    fileUpload(payload) {
      console.log(payload);
      return call(["admin", "file-upload"], {
        method: "POST",
        auth: true,
        body: { file: payload },
      });
    },
  },
  testimonials: {
    tableList(payload = {}) {
      return call(["admin", "testimonial"], {
        method: "GET",
        auth: true,
        params: payload,
      });
    },
    add(payload) {
      return call(["admin", "testimonial"], {
        method: "POST",
        body: payload,
      });
    },
    update(payload, testimonialId) {
      return call(["admin", "testimonial", testimonialId], {
        method: "PATCH",
        body: payload,
      });
    },

    delete(testimonialId) {
      return call(["admin", "investor", testimonialId], {
        method: "DELETE",
        auth: true,
      });
    },
  },
};
export const fileUpload = (formData) => {
  const headers = new Headers();
  headers.append("Authorization", `Bearer ${getUserAuthToken()}`);
  // headers.append("Content-Type", "multipart/form-data");

  const options = {
    method: "POST",
    headers: headers,
    body: formData,
  };
  const url = API_URL + "/admin/file-upload";

  return fetch(url, options)
    .then((response) => response.json()) // Parse response as JSON
    .then((data) => data) // Return the data directly
    .catch((error) => {
      // Handle the error
      console.error(error);
    });
};
